import { firePixel } from "../pixels";
import { DOMAIN, urlParam } from "../config";

//How long the visitor waited before this document arrived, from the browser's
//navigation timing. fetchStart counts everything before the request for this
//page, cross-origin redirects included (the ad network's and the tracking
//link's), which redirectStart/End leave at 0. The CRM keeps the first page of
//a transaction only - the one the tracking link led to.
function navigationTimings() {
	const nav =
		window.performance &&
		performance.getEntriesByType &&
		performance.getEntriesByType("navigation")[0];
	if (!nav) {
		return null;
	}

	const ms = (value) => Math.max(0, Math.round(value));
	const timings = {
		nt_redirect: ms(nav.fetchStart),
		nt_dns: ms(nav.domainLookupEnd - nav.domainLookupStart),
		nt_connect: ms(nav.connectEnd - nav.connectStart),
		nt_wait: ms(nav.responseStart - nav.requestStart),
		nt_ttfb: ms(nav.responseStart),
		nt_protocol: nav.nextHopProtocol || "",
		nt_type: nav.type || "",
	};
	if (performance.timeOrigin > 0) {
		timings.nt_origin = Math.round(performance.timeOrigin);
	}
	if (nav.secureConnectionStart > 0) {
		timings.nt_tls = ms(nav.connectEnd - nav.secureConnectionStart);
	}
	if (nav.responseEnd > 0) {
		timings.nt_download = ms(nav.responseEnd - nav.responseStart);
	}

	const connection = navigator.connection;
	if (connection) {
		if (connection.effectiveType) {
			timings.nt_effective_type = connection.effectiveType;
		}
		if (connection.rtt != null) {
			timings.nt_rtt = connection.rtt;
		}
	}

	return timings;
}

//How far the phone's clock is from the CRM's: the pageView's answer names the
//CRM's time (Server-Timing clock), which falls between the request leaving and
//the answer arriving on the phone's clock. The shortest round trip is the
//surest. Null when the browser does not expose it.
function clockOffset() {
	if (!(performance.timeOrigin > 0) || !performance.getEntriesByType) {
		return null;
	}

	let best = null;
	performance.getEntriesByType("resource").forEach((entry) => {
		if (entry.name.indexOf("/funnels-sdk/get-pixels2") < 0 || !entry.serverTiming || !(entry.requestStart > 0) || !(entry.responseStart > 0)) {
			return;
		}
		const clock = entry.serverTiming.find((timing) => timing.name === "clock");
		const server = clock ? Number(clock.description) : NaN;
		if (!(server > 0)) {
			return;
		}
		const rtt = entry.responseStart - entry.requestStart;
		const middle = performance.timeOrigin + (entry.requestStart + entry.responseStart) / 2;
		if (!best || rtt < best.rtt) {
			best = { offset: Math.round(server - middle), rtt: Math.round(rtt) };
		}
	});

	return best;
}

//Behind Amazon CloudFront with Server-Timing on, the page's own response names
//its location (cdn-pop;desc="DEL51-P2" -> DEL), so no request is needed.
function cloudFrontPop() {
	const nav =
		window.performance &&
		performance.getEntriesByType &&
		performance.getEntriesByType("navigation")[0];
	const entry = nav && nav.serverTiming && nav.serverTiming.find((timing) => timing.name === "cdn-pop");
	return entry && entry.description ? entry.description.slice(0, 3) : "";
}

//Which network and location served this page. CloudFront's Server-Timing first;
//otherwise the landing's own trace (/cdn-cgi/trace) on the connection the page
//already uses. Cloudflare answers it (colo, protocol, TLS); behind CloudFront
//without Server-Timing the origin's 404 comes back with the location in
//x-amz-cf-pop. Nothing when neither answers within 3s.
function edgeInfo() {
	const pop = cloudFrontPop();
	if (pop) {
		return Promise.resolve({ network: "cloudfront", colo: pop });
	}

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), 3000);

	return fetch("/cdn-cgi/trace", { cache: "no-store", signal: controller.signal })
		.then((response) => {
			const pop = response.headers.get("x-amz-cf-pop");
			if (pop) {
				return { network: "cloudfront", colo: pop.slice(0, 3) };
			}
			return (response.ok ? response.text() : Promise.resolve("")).then((text) => {
				const info = {};
				text.split("\n").forEach((line) => {
					const [key, value] = line.split("=");
					if (key === "colo" || key === "http" || key === "tls") {
						info[key] = value;
					}
				});
				if (info.colo) {
					info.network = "cloudflare";
				}
				return info;
			});
		})
		.then((info) => {
			clearTimeout(timer);
			return info;
		})
		.catch(() => {
			clearTimeout(timer);
			return {};
		});
}

//The largest contentful paint, counted from the click like nt_ttfb (Chrome).
//It can still grow until the visitor first taps or types, so the reader is
//called only then. Nothing for a page opened in the background, whose paint
//waits for the tab.
function largestPaint() {
	const types = window.PerformanceObserver && PerformanceObserver.supportedEntryTypes;
	if (!types || types.indexOf("largest-contentful-paint") < 0 || document.visibilityState === "hidden") {
		return null;
	}

	let paint = null;
	const keep = (entries) => {
		if (entries.length) {
			paint = entries[entries.length - 1].startTime;
		}
	};
	const observer = new PerformanceObserver((list) => keep(list.getEntries()));
	observer.observe({ type: "largest-contentful-paint", buffered: true });

	return () => {
		keep(observer.takeRecords());
		observer.disconnect();
		return paint;
	};
}

//The timings travel apart from the pageView, which must leave at once. Where
//the browser reports the largest paint they wait for its final value: the
//visitor's first tap or key (or LCP_WAIT_MS without one), then for the edge
//location; leaving the page sends what is known at once. keepalive lets the
//request out even when the visitor is already leaving.
const LCP_WAIT_MS = 10000;

function sendNavigationTimings() {
	const transactionId = urlParam("transaction_id");
	const timings = navigationTimings();
	if (!transactionId || !timings) {
		return;
	}

	let edge = {};
	const edgeReady = edgeInfo().then((info) => {
		edge = info;
	});
	const readPaint = largestPaint();
	const inputs = ["pointerdown", "keydown"];
	let paint = null;
	let settled = false;
	let sent = false;
	let timer = null;

	const settle = () => {
		if (readPaint && !settled) {
			settled = true;
			paint = readPaint();
		}
	};
	const settleThenSend = () => {
		settle();
		edgeReady.then(send);
	};
	const leave = (event) => {
		if (event.type === "pagehide" || document.visibilityState === "hidden") {
			settle();
			send();
		}
	};

	const send = () => {
		if (sent) {
			return;
		}
		sent = true;
		clearTimeout(timer);
		inputs.forEach((type) => removeEventListener(type, settleThenSend, { capture: true }));
		document.removeEventListener("visibilitychange", leave);
		removeEventListener("pagehide", leave);

		const body = new URLSearchParams({ transaction_id: transactionId });
		Object.keys(timings).forEach((key) => body.set(key, timings[key]));
		if (paint != null) {
			body.set("nt_lcp", Math.max(0, Math.round(paint)));
		}
		const clock = clockOffset();
		if (clock) {
			body.set("nt_clock", clock.offset);
			body.set("nt_clock_rtt", clock.rtt);
		}
		if (edge.network) {
			body.set("nt_edge_network", edge.network);
		}
		if (edge.colo) {
			body.set("nt_colo", edge.colo);
		}
		if (edge.http) {
			body.set("nt_edge_http", edge.http);
		}
		if (edge.tls) {
			body.set("nt_edge_tls", edge.tls);
		}

		fetch(DOMAIN + "/funnels-sdk/navigation-timings", {
			method: "POST",
			body,
			keepalive: true,
		}).catch(() => {});
	};

	if (!readPaint) {
		edgeReady.then(send);
		return;
	}

	timer = setTimeout(settleThenSend, LCP_WAIT_MS);
	inputs.forEach((type) => addEventListener(type, settleThenSend, { capture: true, once: true }));
	document.addEventListener("visibilitychange", leave);
	addEventListener("pagehide", leave);
}

//Fires on every page of a funnel, form or no form. The CRM dedupes the
//pageView pixel per transaction, so entry, packs and checkout can all call
//this and the visitor's affiliate pixel still fires exactly once.
export function pageViewPixel() {
	firePixel("pageView");
	sendNavigationTimings();
}
